"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
class Select {
    static initialized = false;
    static observer = null;
    static init() {
        if (Select.initialized)
            return;
        Select.initialized = true;
        document.querySelectorAll('select').forEach((select) => {
            new CustomSelect(select);
        });
        Select.observeDOMForSelects();
    }
    static observeDOMForSelects() {
        if (Select.observer) {
            Select.observer.disconnect();
        }
        Select.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.addedNodes) {
                    mutation.addedNodes.forEach((node) => {
                        if (node instanceof HTMLElement) {
                            node.querySelectorAll('select').forEach((select) => {
                                new CustomSelect(select);
                            });
                        }
                    });
                }
            });
        });
        Select.observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}
const makeElement = (tag, classes = [], attributes = {}) => {
    const element = document.createElement(tag);
    element.classList.add(...classes);
    Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value);
    });
    return element;
};
class CustomSelect {
    select;
    wrapper = makeElement('div', ['custom-select']);
    dropdown = makeElement('div', ['custom-select__dropdown']);
    item = makeElement('div', ['item']);
    isOpen = false;
    isClearable = false;
    isSearchable = false;
    boundOutsideClickListener;
    displayElement;
    _set_placeholder_and_clear_display_element = () => {
        this.displayElement.focus();
        this.displayElement.textContent = '';
        this.displayElement.setAttribute('placeholder', this.select.options[this.select.selectedIndex]?.textContent || 'Search...');
    };
    constructor(select) {
        this.select = select;
        const ignoredClasses = ['vanilla', 'multi-checkbox-select', 'swal2-select', 'select2-hidden-accessible', 'browser-default', 'fk_multiselect'];
        if (this.select.dataset.customSelect || ignoredClasses.some(cls => this.select.classList.contains(cls)))
            return;
        this.select.dataset.customSelect = 'true';
        this.select.style.display = 'none';
        this.isSearchable = this.select.dataset.search === 'true';
        this.initElement();
        this.select.insertAdjacentElement('afterend', this.wrapper);
        this.closeDropdown = this.closeDropdown.bind(this);
        this.boundOutsideClickListener = this.outsideClickListener.bind(this);
        this.observeSelectElement();
    }
    async closeDropdown() {
        await gsap.to(this.dropdown, {
            opacity: 0,
            duration: 0.15,
            clearProps: 'opacity',
        });
        this.dropdown.remove();
        this.isOpen = false;
        this.dropdown.classList.remove('flip');
        document.removeEventListener('click', this.boundOutsideClickListener);
        if (this.isSearchable && this.displayElement instanceof HTMLInputElement) {
            this.displayElement.value = '';
            this.updateDropdownItems();
        }
    }
    outsideClickListener(e) {
        if (e.target.closest('.custom-select') === this.wrapper || e.target === this.select)
            return;
        this.closeDropdown();
    }
    setDropdownPosition() {
        if (!this.wrapper.isConnected) {
            this.closeDropdown();
            return;
        }
        const rect = this.wrapper.getBoundingClientRect();
        const margin = 4;
        if (rect.bottom + this.dropdown.clientHeight > window.innerHeight) {
            this.dropdown.style.top = `${rect.top - this.dropdown.clientHeight - margin}px`;
            this.dropdown.classList.add('flip');
        }
        else {
            this.dropdown.style.top = `${rect.bottom + margin}px`;
        }
        this.dropdown.style.left = `${rect.left}px`;
        this.dropdown.style.minWidth = `${rect.width}px`;
    }
    openDropdown() {
        if (this.isOpen)
            return;
        this.isOpen = true;
        document.body.appendChild(this.dropdown);
        this.setDropdownPosition();
        if (this.isSearchable) {
            this._set_placeholder_and_clear_display_element();
        }
        document.addEventListener('click', this.boundOutsideClickListener);
    }
    createItem(option) {
        const item = this.item.cloneNode();
        item.textContent = option.textContent || '';
        item.dataset.value = option.value;
        item.onclick = () => {
            this.selectItem(item);
            this.closeDropdown();
        };
        return item;
    }
    initElement() {
        this.updateDropdownItems();
        const selectedOption = this.select.options[this.select.selectedIndex];
        const displayText = selectedOption?.textContent || '';
        const placeholderText = this.select.getAttribute('placeholder') || 'Select an item';
        if (this.isSearchable) {
            const div = document.createElement('div');
            div.classList.add('display', 'searchable');
            div.contentEditable = 'true';
            div.setAttribute('role', 'textbox');
            div.setAttribute('aria-label', 'Search');
            div.setAttribute('placeholder', placeholderText);
            this.displayElement = div;
            div.addEventListener('input', () => {
                this.filterItems(div.textContent || '');
            });
            const resizeObserver = new ResizeObserver(() => {
                this.setDropdownPosition();
            });
            resizeObserver.observe(div);
        }
        else {
            this.displayElement = makeElement('div', ['display']);
            this.displayElement.textContent = displayText;
        }
        if (this.select.dataset.displayClass) {
            const classes = this.select.dataset.displayClass.split(' ').map(cls => cls.trim());
            this.displayElement.classList.add(...classes, 'no-style');
        }
        if (this.isSearchable) {
            this._set_placeholder_and_clear_display_element();
        }
        this.displayElement.setAttribute('data-value', this.select.value);
        this.wrapper.appendChild(this.displayElement);
        this.wrapper.addEventListener('click', () => {
            if (!this.isOpen) {
                this.openDropdown();
            }
        });
        this.select.addEventListener('change', () => {
            const option = this.select.options[this.select.selectedIndex];
            this.displayElement.textContent = option?.textContent || '';
            this.displayElement.setAttribute('data-value', this.select.value);
        });
        document.querySelector('#main_container')?.addEventListener('scroll', () => this.closeDropdown());
        this.select.form?.addEventListener('reset', () => {
            setTimeout(() => {
                const option = this.select.options[this.select.selectedIndex];
                if (this.isSearchable && this.displayElement instanceof HTMLInputElement) {
                    this._set_placeholder_and_clear_display_element();
                }
                else {
                    this.displayElement.textContent = option?.textContent || '';
                }
                this.displayElement.setAttribute('data-value', this.select.value);
            }, 0);
        });
    }
    updateDropdownItems() {
        this.dropdown.innerHTML = '';
        const items = Array.from(this.select.options).map(option => {
            if (option.disabled && option.value === '')
                return null;
            return this.createItem(option);
        }).filter(Boolean);
        items.forEach(item => item.classList.add('custom-select__option'));
        this.dropdown.append(...items);
    }
    filterItems(query) {
        const filter = query.toLowerCase();
        const items = this.dropdown.querySelectorAll('.custom-select__option');
        items.forEach(item => {
            const text = item.textContent?.toLowerCase() || '';
            item.style.display = text.includes(filter) ? 'block' : 'none';
        });
        if (this.dropdown.querySelectorAll('.custom-select__option[style*="display: block"]').length === 0) {
            if (!this.dropdown.querySelector('.no-results')) {
                const noResults = makeElement('div', ['no-results']);
                noResults.textContent = 'No results found';
                this.dropdown.appendChild(noResults);
            }
        }
        else {
            const noResults = this.dropdown.querySelector('.no-results');
            if (noResults)
                noResults.remove();
        }
    }
    observeSelectElement() {
        const observer = new MutationObserver(() => {
            this.updateDropdownItems();
        });
        observer.observe(this.select, {
            childList: true,
            subtree: false,
        });
    }
    selectItem(item) {
        this.select.value = item.dataset.value || '';
        const changeEvent = new Event('change', { bubbles: true });
        this.select.dispatchEvent(changeEvent);
        const reactOnChange = this.select._reactEvents?.onChange;
        if (reactOnChange) {
            reactOnChange({ target: this.select });
        }
    }
}
exports.default = Select;
